import {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';
import {QueryReturnValue} from '@reduxjs/toolkit/dist/query/baseQueryTypes';

import {userJobDispatcher} from 'app/jobs/userJobDispatcher';
import {sweetAPI} from 'app/store/sweet-api';
import {
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {
  BulkAddTagsType,
  BulkDeleteTagsParams,
  TaggedItemType,
  TagsDashboardFetchParamsType,
  TagsDashboardLocationParamsType,
} from 'app/tags/types';
import {TAGGEDITEMS_API_URL, TAGS_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {TAGS_RTK_TAGS} from 'app/utils/rtk-tags';

import {DEFAULT_PAGE_SIZE} from '../utils/constants';

import {
  ListResponseType,
  TagSerializedType,
  UserJobSerializedType,
} from 'sweet-types';

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [TAGS_RTK_TAGS.TAGS],
  })

  .injectEndpoints({
    endpoints: (build) => ({
      tags: build.query<
        ListResponseType<TagSerializedType>,
        TagsDashboardFetchParamsType
      >({
        query: (params) => ({
          url: TAGS_API_URL,
          params: params ?? {},
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: {
              fallback: () => gt('Could not fetch tags.'),
              byStatus: {},
              silenceByStatus: {404: true},
            },
          })(args, api);
        },
        providesTags: providesTagsByURL(TAGS_RTK_TAGS.TAGS),
      }),
      updateTag: build.mutation<
        TagSerializedType,
        Pick<TagSerializedType, 'id' | 'url' | 'name'>
      >({
        query: (tag) => ({
          url: tag.url,
          method: 'PATCH',
          body: tag,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Could not update tag.'),
            onSuccess: () => gt('Tag renamed'),
          })(args, api);
        },
        invalidatesTags: () => [{type: TAGS_RTK_TAGS.TAGS, id: 'list'}],
      }),
      deleteTag: build.mutation<unknown, string>({
        query: (url) => ({
          url,
          method: 'DELETE',
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Could not delete tag.'),
            onSuccess: () => gt('Tag deleted'),
          })(args, api);
        },
        invalidatesTags: () => [{type: TAGS_RTK_TAGS.TAGS, id: 'list'}],
      }),
      removeTag: build.mutation<unknown, string>({
        query: (url) => ({
          url,
          method: 'DELETE',
        }),
        invalidatesTags: () => [{type: TAGS_RTK_TAGS.TAGS, id: 'list'}],
        onQueryStarted: (args, api) => {
          showMessages({
            onError: () => gt('Could not remove tag.'),
            onSuccess: () => gt('Tag removed.'),
          })(args, api);
        },
      }),
      addTags: build.mutation<unknown, Array<TaggedItemType>>({
        query: (tags) => ({
          url: TAGGEDITEMS_API_URL,
          method: 'POST',
          body: tags,
        }),
        onQueryStarted: (args, api) => {
          showMessages({
            onError: () => gt('Could not add tag.'),
            onSuccess: () => gt('Tag added.'),
          })(args, api);
        },
        invalidatesTags: () => [{type: TAGS_RTK_TAGS.TAGS, id: 'list'}],
      }),
      bulkAddTags: build.mutation<
        UserJobSerializedType & {status: 'completed'},
        BulkAddTagsType
      >({
        queryFn: (
          {documents},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${TAGGEDITEMS_API_URL}bulk_add/`,
                    method: 'POST',
                    body: {
                      documents,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [{type: TAGS_RTK_TAGS.TAGS, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onSuccess: () => [gt('Tag added.'), {level: 'success'}],
            onError: () => gt('Could not add tag.'),
          })(args, api);
        },
      }),
      bulkRemoveTags: build.mutation<
        UserJobSerializedType & {status: 'completed'},
        BulkDeleteTagsParams
      >({
        queryFn: (
          {tagged_items},
          api,
          extraOptions,
          baseQuery
        ): Promise<
          QueryReturnValue<
            UserJobSerializedType & {status: 'completed'},
            FetchBaseQueryError,
            any
          >
        > => {
          return api
            .dispatch(
              userJobDispatcher(
                (
                  baseQuery({
                    url: `${TAGGEDITEMS_API_URL}bulk_remove/`,
                    method: 'POST',
                    body: {
                      tagged_items,
                    },
                  }) as Promise<
                    QueryReturnValue<
                      {job: UserJobSerializedType},
                      FetchBaseQueryError,
                      any
                    >
                  >
                ).then((response) => {
                  if (response.data) {
                    return response.data;
                  } else {
                    throw response.error;
                  }
                })
              )
            )
            .job.then(
              (job) => ({data: job}),
              (error) => ({error})
            );
        },
        invalidatesTags: () => [{type: TAGS_RTK_TAGS.TAGS, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onSuccess: () => [gt('Tag removed.'), {level: 'success'}],
            onError: () => gt('Could not remove tag.'),
          })(args, api);
        },
      }),
    }),
  });

export const {
  useTagsQuery: useTags,
  useDeleteTagMutation: useDeleteTag,
  useUpdateTagMutation: useUpdateTag,
  useRemoveTagMutation: useRemoveTag,
  useAddTagsMutation: useAddTags,
  useBulkAddTagsMutation: useBulkAddTags,
  useBulkRemoveTagsMutation: useBulkRemoveTags,
} = extendedAPI;

export const convertLocationParamsToFetchParams = (
  query: TagsDashboardLocationParamsType
): TagsDashboardFetchParamsType => ({
  page: query.page != null ? query.page : '1',
  page_size: query.page_size != null ? query.page_size : `${DEFAULT_PAGE_SIZE}`,
  search: query.search,
  slug: query.slug,
  exclude_slug: query.exclude_slug,
});
